/**
 * Site header (sections/header.liquid + snippets/header-*.liquid):
 * - seamless announcement marquee,
 * - full-screen menu dialog #site-menu: inert/aria-hidden toggle, focus trap,
 *   Escape / close button / backdrop click to close, focus back to the toggle.
 */
(() => {
  'use strict';

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* Seamless marquee: fill ≥2× viewport, then duplicate that strip.
     Animate by exactly half the track (= one filled strip). Docs pattern:
     identical halves + linear translate; gap appears when content < viewport.
     Only the original group stays readable; every clone is aria-hidden. */
  const setupMarquee = (chrome) => {
    const root = chrome.querySelector('[data-announcement]');
    const track = chrome.querySelector('[data-announcement-track]');
    const source = chrome.querySelector('[data-announcement-group]');
    if (!root || !track || !source) return;

    track.style.animation = 'none';
    while (track.children.length > 1) track.removeChild(track.lastElementChild);

    const minWidth = Math.max(root.offsetWidth, 1) * 2;
    let guard = 0;
    while (track.scrollWidth < minWidth && guard < 24) {
      const clone = source.cloneNode(true);
      clone.removeAttribute('data-announcement-group');
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
      guard += 1;
    }

    const unitWidth = track.scrollWidth;
    const half = document.createDocumentFragment();
    [...track.children].forEach((node) => {
      const clone = node.cloneNode(true);
      clone.removeAttribute('data-announcement-group');
      clone.setAttribute('aria-hidden', 'true');
      half.appendChild(clone);
    });
    track.appendChild(half);

    root.style.setProperty('--announcement-shift', unitWidth + 'px');
    /* ~40px/s — duration scales with content length */
    const duration = Math.max(18, unitWidth / 40);
    root.style.setProperty('--announcement-duration', duration.toFixed(1) + 's');
    track.style.animation = '';
  };

  const initMenu = (signal) => {
    const menu = document.querySelector('[data-site-menu]');
    const openBtn = document.querySelector('[data-site-menu-open]');
    const closeBtn = document.querySelector('[data-site-menu-close]');
    if (!menu || !openBtn) return;

    const isOpen = () => menu.classList.contains('is-on');
    const focusables = () => [...menu.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);

    const setMenu = (on) => {
      const focusWasInside = menu.contains(document.activeElement);
      menu.classList.toggle('is-on', on);
      menu.setAttribute('aria-hidden', on ? 'false' : 'true');
      menu.inert = !on;
      openBtn.setAttribute('aria-expanded', on ? 'true' : 'false');
      document.body.classList.toggle('menu-lock', on);
      if (on) {
        if (closeBtn) closeBtn.focus();
      } else if (focusWasInside || document.activeElement === document.body) {
        openBtn.focus();
      }
    };

    // Keep Tab / Shift+Tab inside the open menu (it is aria-modal, the page behind is not inert).
    const trapFocus = (e) => {
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!menu.contains(active)) {
        // Another modal (popup, mini-cart) stacked on top owns the focus — leave it alone.
        if (active && active !== document.body && active.closest('[aria-modal="true"]')) return;
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    openBtn.addEventListener('click', () => setMenu(true), { signal });
    if (closeBtn) closeBtn.addEventListener('click', () => setMenu(false), { signal });
    menu.addEventListener(
      'click',
      (e) => {
        if (e.target === menu) setMenu(false);
      },
      { signal }
    );
    document.addEventListener(
      'keydown',
      (e) => {
        if (!isOpen()) return;
        if (e.key === 'Escape') setMenu(false);
        else if (e.key === 'Tab') trapFocus(e);
      },
      { signal }
    );
  };

  let controller = null;

  const init = () => {
    const chrome = document.querySelector('[data-site-chrome]');
    if (!chrome || chrome.dataset.headerReady) return;
    chrome.dataset.headerReady = 'true';

    if (controller) controller.abort();
    controller = new AbortController();
    const { signal } = controller;

    chrome.classList.add('is-visible');

    setupMarquee(chrome);
    let resizeTimer = 0;
    window.addEventListener(
      'resize',
      () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => setupMarquee(chrome), 150);
      },
      { passive: true, signal }
    );

    initMenu(signal);
  };

  init();

  // Theme editor re-renders the section: bind the new markup, drop the old listeners.
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('[data-site-chrome]')) init();
  });
  document.addEventListener('shopify:section:unload', (e) => {
    if (controller && e.target.querySelector('[data-site-chrome]')) {
      controller.abort();
      controller = null;
      document.body.classList.remove('menu-lock');
    }
  });
})();
